/**
 * @vuelto-surface
 * mobile: ok (1 columna + sheet)
 * desktop: ok (columnas full-width + dialog)
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import {
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { resolveSessionEmployee } from '../../auth/application/sessionEmployeeResolver';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { useScreenLoad } from '../../shared/ui/hooks-screen/useScreenLoad'
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState'
import { showError, showSuccess } from '../../shared/ui/appToast';
import { supabase } from '../../shared/supabase/supabaseClient';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { useTabWorkspaceLayout } from '../../shared/layout/useContentLayout';
import { CONTENT_MAX_WIDTH_FORM } from '../../shared/layout/breakpoints';
import { formatArPhoneDisplay, telHref } from '@shared/phone';
import { spacing } from '../../shared/theme/spacing';
import { agenda, agendaDotColor, agendaFonts, agendaPaperSheet } from '../domain/agendaTheme';
import { filterAgendaOwnScope } from '../domain/filterAgendaOwnScope';
import type { Appointment, AppointmentResource, AppointmentStatus, AppointmentWaitlistEntry, BookingSettings, ResourceTimeOff, ServiceProductOption } from '../domain/types';
import { waitlistEntryMatch, type WaitlistEntryMatch } from '../domain/waitlistMatch';
import {
  dateAtMinutes,
  defaultDesktopResourceIds,
  DESKTOP_MAX_COLS,
  minutesToHm,
  reconcileDesktopResourceIds,
} from '../domain/dayBoardLayout';
import {
  cancelAppointment,
  clearAppointmentArrived,
  getAppointmentById,
  getBookingSettings,
  getCustomerAppointmentStats,
  getQuoteEstimatedSessions,
  getQuoteEstimatedSessionsByIds,
  listBranchAppointments,
  listBranchResourceTimeOff,
  listBranchWaitlist,
  listResources,
  listServiceProducts,
  markAppointmentArrived,
  offerWaitlistEntry,
  updateAppointment,
  updateStatus,
} from '../infra/appointmentsRepo';
import { formatQuoteSessionMeta } from '../../quotes/domain/sessionProgress';
import { AgendaVisibleBar } from '../components/AgendaVisibleBar';
import { AgendaStaffPickerModal } from '../components/AgendaStaffPickerModal';
import { openAppointmentWhatsAppReminder } from '../application/appointmentWhatsAppReminder';
import {
  getAppointmentReminderSentAt,
  markAppointmentReminderSent,
} from '../application/appointmentReminderStorage';
import { setPendingAppointmentCheckout } from '../application/appointmentPosBridge';
import { findWorkOrderByAppointment } from '../../work-orders/infra/workOrdersRepo';
import { SaleDetailModal } from '../../sales/public-api';
import { openCustomerDetail } from '../../customers/public-api';
import { formatMoney } from '../../shared/format/public-api';
import { ticketColors } from '../../shared/theme/ticketColors';
import {
  AppointmentFormModal,
  type AppointmentFormPrefill,
} from '../components/AppointmentFormModal';
import { ServicePackagesModal } from '../components/ServicePackagesModal';
import {
  AppointmentsDayBoard,
  type DayBoardMoveTarget,
  type DayBoardSlotPress,
} from './AppointmentsDayBoard';
import { AppointmentsDayList } from '../components/AppointmentsDayList';
import { AgendaDayKpis } from '../components/AgendaDayKpis';
import { AppointmentWaitlistPanel } from '../components/AppointmentWaitlistPanel';
import { AppointmentsMonthBoard } from './AppointmentsMonthBoard';
import { AppointmentsWeekBoard, weekDaysFrom } from './AppointmentsWeekBoard';
import { computeDayBoardKpis } from '../domain/dayBoardLayout';
import { TicketMoreActionsSheet, type TicketMoreAction } from '../../shared/ticket/TicketMoreActionsSheet';
import { useAppointmentsRealtime } from '../hooks/useAppointmentsRealtime';

type AgendaViewMode = 'day' | 'week' | 'month';

const STATUS_LABELS: Record<AppointmentStatus, string> = {
  requested: 'Solicitado',
  confirmed: 'Confirmado',
  completed: 'Completado',
  cancelled: 'Cancelado',
  no_show: 'No vino',
};

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

function endOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

function addDays(d: Date, n: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + n);
  return next;
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function formatDayLabel(d: Date): string {
  return d.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' });
}

function formatWeekLabel(d: Date): string {
  const days = weekDaysFrom(d);
  const a = days[0];
  const b = days[6];
  return `${a.getDate()}–${b.getDate()} ${b.toLocaleDateString('es-AR', { month: 'short' })}`;
}

function formatMonthLabel(d: Date): string {
  return d.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatShortDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

type WalkInPrefill = AppointmentFormPrefill;

type AppointmentsScreenProps = {
  /** When embedded in OrdersInbox, pass `OrdersInbox` so the web header refresh button works. */
  refreshRouteName?: string;
};

type AppointmentsRouteParams = {
  /** Desde Inicio → “Hoy en agenda”: saltar a hoy en vista día. */
  focusToday?: boolean;
  /** Desde Inicio → «Nuevo turno»: abre el formulario de creación. */
  openCreate?: boolean;
  /** Desde push notification: abrir el detalle de un turno específico. */
  focusAppointmentId?: string;
  /** Desde push notification: fecha del turno (ISO) para saltar al día correcto. */
  focusDate?: string;
};

export function AppointmentsScreen({
  refreshRouteName = 'Appointments',
}: AppointmentsScreenProps = {}) {
  const navigation = useNavigation();
  const route = useRoute();
  const routeParams = route.params as AppointmentsRouteParams | undefined;
  const focusToday = routeParams?.focusToday === true;
  const openCreateParam = routeParams?.openCreate === true;
  const focusAppointmentId = routeParams?.focusAppointmentId;
  const focusDate = routeParams?.focusDate;
  const { layoutWide: isWide } = useTabWorkspaceLayout();
  const { entitlements } = usePlanEntitlements();
  const orgId = entitlements?.organization_id;
  const branchId = useSessionStore((s) => s.currentBranchId);
  const branchName = useSessionStore((s) => s.branches.find((b) => b.id === s.currentBranchId)?.name);
  const { canView, canEdit, canManageAgenda, canViewOthers } = useBranchPermission('Agenda');
  const hasAppointments = useHasBusinessCapability('appointments');
  const hasServicePackages = useHasBusinessCapability('service_packages');
  const hasWorkOrders = useHasBusinessCapability('work_orders');
  const isOwner = entitlements?.is_owner === true;
  const canSeeAllResources = canViewOthers || isOwner;
  const [noLinkedResource, setNoLinkedResource] = useState(false);

  const [day, setDay] = useState(() => startOfLocalDay(new Date()));
  const [viewMode, setViewMode] = useState<AgendaViewMode>('day');
  /** Tablero|Lista dentro de la vista día (proto agenda-ux-v1 — lista recepción). */
  const [dayMode, setDayMode] = useState<'board' | 'list'>('board');
  /** Pref local — muestra cancelados apagados. */
  const [showCancelled, setShowCancelled] = useState(false);

  useEffect(() => {
    if (!focusToday) return;
    setDay(startOfLocalDay(new Date()));
    setViewMode('day');
    navigation.setParams({ focusToday: undefined } as never);
  }, [focusToday, navigation]);
  const [desktopSelectedIds, setDesktopSelectedIds] = useState<string[]>([]);
  const [mobileResourceId, setMobileResourceId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [resources, setResources] = useState<AppointmentResource[]>([]);
  const [items, setItems] = useState<Appointment[]>([]);
  const [timeOffs, setTimeOffs] = useState<ResourceTimeOff[]>([]);
  const [services, setServices] = useState<ServiceProductOption[]>([]);
  const [loading, setLoading] = useState(true);
  const loadSequence = useRef(0);
  const [quoteSessionsTotalById, setQuoteSessionsTotalById] = useState<Map<string, number>>(
    () => new Map(),
  );
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formPrefill, setFormPrefill] = useState<WalkInPrefill | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [selected, setSelected] = useState<Appointment | null>(null);
  const [detailSaleId, setDetailSaleId] = useState<string | null>(null);
  const [packagesOpen, setPackagesOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  /** Modo mover (proto agenda-ux-v1): turno en tránsito hasta tocar un hueco destino. */
  const [moving, setMoving] = useState<Appointment | null>(null);
  const [moveConfirm, setMoveConfirm] = useState<DayBoardMoveTarget | null>(null);
  /** Touch safety: primer tap marca el destino (ghost), segundo confirma. */
  const [movePreview, setMovePreview] = useState<DayBoardMoveTarget | null>(null);
  const [moveBusy, setMoveBusy] = useState(false);
  /** PELU-09: cola de espera — settings para el gate + TTL, entries para panel/banner. */
  const [bookingSettings, setBookingSettings] = useState<BookingSettings | null>(null);
  const [waitlist, setWaitlist] = useState<AppointmentWaitlistEntry[]>([]);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistSlotFilter, setWaitlistSlotFilter] = useState<{
    resourceId: string;
    startMinutes: number;
  } | null>(null);
  const [offerBusyId, setOfferBusyId] = useState<string | null>(null);


  const productNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of services) map.set(s.id, s.name);
    return map;
  }, [services]);

  const resourceNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of resources) map.set(r.id, r.displayName);
    return map;
  }, [resources]);

  /** Mismo color que los chips de VIENDO — identifica la agenda en vistas multi. */
  const resourceColorById = useMemo(() => {
    const map = new Map<string, string>();
    resources.forEach((r, i) => map.set(r.id, agendaDotColor(r.color, i)));
    return map;
  }, [resources]);

  const range = useMemo(() => {
    if (viewMode === 'week') {
      const days = weekDaysFrom(day);
      return { from: startOfLocalDay(days[0]), to: endOfLocalDay(days[6]) };
    }
    if (viewMode === 'month') {
      const from = new Date(day.getFullYear(), day.getMonth(), 1);
      const to = endOfLocalDay(new Date(day.getFullYear(), day.getMonth() + 1, 0));
      return { from, to };
    }
    return { from: startOfLocalDay(day), to: endOfLocalDay(day) };
  }, [day, viewMode]);

  const lastLoadStartedAt = useRef(0);
  const load = useCallback(async (opts?: { force?: boolean; silent?: boolean }) => {
    if (!branchId || !hasAppointments || !canView) return;
    const seq = ++loadSequence.current;
    lastLoadStartedAt.current = Date.now();
    if (!opts?.silent) setLoading(true);
    try {
      const [res, appts, offs, sessionEmp, authResult, settings, wl] = await Promise.all([
        listResources(branchId),
        listBranchAppointments(branchId, range.from.toISOString(), range.to.toISOString(), null),
        listBranchResourceTimeOff(branchId, range.from.toISOString(), range.to.toISOString()),
        canSeeAllResources
          ? Promise.resolve({ employeeId: null, isEmployee: false, isActive: true })
          : resolveSessionEmployee(),
        supabase.auth.getUser(),
        getBookingSettings(branchId).catch(() => null),
        listBranchWaitlist(branchId).catch(() => []),
      ]);
      if (seq !== loadSequence.current) return; // stale response
      setBookingSettings(settings);
      setWaitlist(wl);
      const authUserId = authResult.data.user?.id ?? null;
      const ownerScope = canSeeAllResources || !sessionEmp?.isEmployee || res.some((r) => r.userId === authUserId);
      const scoped = filterAgendaOwnScope({
        resources: res.filter((r) => r.isActive),
        appointments: appts,
        timeOffs: offs,
        canViewOthers: ownerScope,
        sessionEmployeeId: sessionEmp?.employeeId ?? null,
      });
      const active = scoped.resources;
      setNoLinkedResource(scoped.noLinkedResource);
      setResources(active);
      setItems(scoped.appointments);
      setTimeOffs(scoped.timeOffs);
      setSelected((prev) => {
        if (!prev) return null;
        return scoped.appointments.find((a) => a.id === prev.id) ?? null;
      });
      const dow = day.getDay();
      setDesktopSelectedIds((prev) => {
        if (prev.length > 0) return reconcileDesktopResourceIds(prev, active, dow);
        const defaults = defaultDesktopResourceIds(active, dow);
        return (defaults.length > 0 ? defaults : active.map((r) => r.id)).slice(0, DESKTOP_MAX_COLS);
      });
      setMobileResourceId((prev) => {
        if (prev && active.some((r) => r.id === prev)) return prev;
        // Default: primera agenda con horario ese día (evita arrancar en "Cerrado hoy").
        const withHours = active.find((r) =>
          r.hours.some((h) => h.dayOfWeek === dow && h.endTime > h.startTime),
        );
        return withHours?.id ?? active[0]?.id ?? null;
      });
    } catch (e) {
      if (seq !== loadSequence.current) return; // stale error
      presentUnexpectedFailure({ module: 'appointments', op: 'agenda', error: e, title: 'Agenda', userMessage: 'No se pudo cargar la agenda.' });
    } finally {
      if (seq === loadSequence.current && !opts?.silent) setLoading(false);
    }
  }, [branchId, canSeeAllResources, canView, day, hasAppointments, range.from, range.to]);

  useScreenLoad(refreshRouteName, load, { refreshOnFocus: false });

  useEffect(() => {
    void load();
  }, [load]);

  // refreshOnFocus está off para no flashear el spinner, pero las rutas hijas
  // (Configurar agendas, etc.) mutan recursos/horarios/servicios sin disparar
  // realtime → al volver, recarga silenciosa siempre (crear/editar agenda,
  // horarios, servicios o desactivar quedaban stale hasta F5). El dedupe por
  // timestamp evita el doble fetch: el effect también refirea al cambiar
  // `load` (día/rango) mientras la pantalla está enfocada.
  useFocusEffect(
    useCallback(() => {
      if (Date.now() - lastLoadStartedAt.current < 1500) return;
      void load({ silent: true });
    }, [load])
  );

  // Realtime: recarga silenciosa cuando un turno cambia desde el portal u otro dispositivo.
  const realtimeResourceIds = useMemo(() => resources.map((r) => r.id), [resources]);
  useAppointmentsRealtime(
    orgId ?? null,
    () => void load({ silent: true }),
    branchId,
    realtimeResourceIds,
  );

  useEffect(() => {
    const ids = [
      ...new Set(
        items
          .map((a) => a.quoteRequestId)
          .filter((id): id is string => typeof id === 'string' && id.length > 0),
      ),
    ];
    if (ids.length === 0) {
      setQuoteSessionsTotalById(new Map());
      return;
    }
    let cancelled = false;
    void getQuoteEstimatedSessionsByIds(ids)
      .then((map) => {
        if (!cancelled) setQuoteSessionsTotalById(map);
      })
      .catch(() => {
        if (!cancelled) setQuoteSessionsTotalById(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [items]);

  useEffect(() => {
    if (!orgId) return;
    void listServiceProducts(orgId, branchId)
      .then(setServices)
      .catch(() => setServices([]));
  }, [orgId, branchId]);

  // Desde push notification: saltar al día del turno
  useEffect(() => {
    if (!focusDate) return;
    setDay(startOfLocalDay(new Date(focusDate)));
    setViewMode('day');
    navigation.setParams({ focusDate: undefined } as never);
  }, [focusDate, navigation]);

  // Desde push notification: abrir el detalle del turno una vez cargados los items del día
  useEffect(() => {
    if (!focusAppointmentId || items.length === 0) return;
    const appt = items.find((a) => a.id === focusAppointmentId);
    if (appt) {
      setSelected(appt);
    }
    // Clear param whether found or not — prevents stale re-trigger on every items change
    navigation.setParams({ focusAppointmentId: undefined } as never);
  }, [focusAppointmentId, items, navigation]);

  const onStatus = async (id: string, status: AppointmentStatus) => {
    if (!canEdit) return;
    try {
      if (status === 'cancelled') {
        await cancelAppointment(id);
      } else {
        await updateStatus(id, status);
      }
      await load({ silent: true });
    } catch (e) {
      presentUnexpectedFailure({ module: 'appointments', op: 'agenda', error: e, title: 'Agenda', userMessage: 'No se pudo actualizar el turno.' });
    }
  };

  const openCreate = (prefill?: WalkInPrefill) => {
    if (!canEdit) return;
    setFormMode('create');
    setEditingAppointment(null);
    setFormPrefill(prefill ?? null);
    setFormOpen(true);
  };

  // Deep link Inicio → «Nuevo turno»: espera a canEdit (permisos resuelven async).
  useEffect(() => {
    if (!openCreateParam || !canEdit) return;
    navigation.setParams({ openCreate: undefined } as never);
    setFormMode('create');
    setEditingAppointment(null);
    setFormPrefill(null);
    setFormOpen(true);
  }, [openCreateParam, canEdit, navigation]);

  const openEdit = (appointment: Appointment) => {
    if (!canEdit) return;
    setFormMode('edit');
    setEditingAppointment(appointment);
    setFormPrefill(null);
    setSelected(null);
    setDay(startOfLocalDay(new Date(appointment.startsAt)));
    setFormOpen(true);
    void getAppointmentById(appointment.id)
      .then((full) => {
        if (full) setEditingAppointment(full);
      })
      .catch(() => {
        /* keep list row */
      });
  };

  const onPressSlot = (slot: DayBoardSlotPress) => {
    openCreate({
      resourceId: slot.resourceId,
      startsAt: dateAtMinutes(day, slot.startMinutes),
    });
  };

  // Mover tiene sentido solo en vista día — salir al cambiar de vista.
  useEffect(() => {
    if (viewMode !== 'day') {
      setMoving(null);
      setMoveConfirm(null);
      setMovePreview(null);
    }
  }, [viewMode]);

  const canMoveAppointment = (a: Appointment) =>
    canEdit && (a.status === 'requested' || a.status === 'confirmed');

  const startMove = (a: Appointment) => {
    if (!canMoveAppointment(a)) return;
    setSelected(null);
    setMoveConfirm(null);
    setMovePreview(null);
    setDay(startOfLocalDay(new Date(a.startsAt)));
    setViewMode('day');
    setDayMode('board');
    setMoving(a);
  };

  const doMove = async (target: DayBoardMoveTarget) => {
    const a = moving;
    if (!a || moveBusy) return;
    setMoveBusy(true);
    try {
      const mins = durationMinutes(a.startsAt, a.endsAt);
      const start = dateAtMinutes(day, target.startMinutes);
      const end = new Date(start.getTime() + mins * 60_000);
      await updateAppointment({
        appointmentId: a.id,
        resourceId: target.resourceId,
        productId: a.productId,
        productIds: a.items?.map((i) => i.productId),
        customerId: a.customerId,
        customerName: a.customerName,
        customerPhone: a.customerPhone,
        customerEmail: a.customerEmail,
        startsAt: start.toISOString(),
        endsAt: end.toISOString(),
        notes: a.notes,
        amountTotal: a.amountTotal,
      });
      setMoving(null);
      setMoveConfirm(null);
      setMovePreview(null);
      await load();
      showSuccess('Turno reagendado.');
    } catch (e) {
      // El repo ya reportó (overlap → expected) y mapeó el mensaje.
      showError('Agenda', e instanceof Error ? e.message : 'No se pudo reagendar el turno.');
    } finally {
      setMoveBusy(false);
    }
  };

  /**
   * Proto: mismo día + misma agenda → directo; cambia día o agenda → confirm.
   * Touch: sin hover-ghost el primer tap commiteaba un movimiento (y un push)
   * — ahora el primer tap pinta el ghost y el segundo confirma.
   */
  const onPressMoveTarget = (target: DayBoardMoveTarget) => {
    const a = moving;
    if (!a || moveBusy) return;
    if (
      movePreview == null ||
      movePreview.resourceId !== target.resourceId ||
      movePreview.startMinutes !== target.startMinutes
    ) {
      setMovePreview(target);
      return;
    }
    setMovePreview(null);
    const sameDay = isSameLocalDay(new Date(a.startsAt), day);
    const sameResource = target.resourceId === a.resourceId;
    if (sameDay && sameResource) {
      void doMove(target);
    } else {
      setMoveConfirm(target);
    }
  };

  const today = startOfLocalDay(new Date());
  const viewingToday = isSameLocalDay(day, today);
  const viewingCurrentWeek =
    viewMode === 'week' && weekDaysFrom(today).some((d) => isSameLocalDay(d, day));
  const viewingCurrentMonth =
    viewMode === 'month' && day.getFullYear() === today.getFullYear() && day.getMonth() === today.getMonth();
  const hoyDisabled = viewingToday || viewingCurrentWeek || viewingCurrentMonth;
  const dayOfWeek = day.getDay();

  const visibleResources = useMemo(() => {
    if (!isWide) {
      const id = mobileResourceId ?? resources[0]?.id;
      return resources.filter((r) => r.id === id).slice(0, 1);
    }
    const order = new Map(desktopSelectedIds.map((id, i) => [id, i]));
    return resources
      .filter((r) => order.has(r.id))
      .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0))
      .slice(0, DESKTOP_MAX_COLS);
  }, [isWide, mobileResourceId, resources, desktopSelectedIds]);

  /** Week/month honor the VIENDO selection (mobile picks one agenda at a time). */
  const boardResourceIds = useMemo(() => {
    if (!isWide) {
      const id = mobileResourceId ?? resources[0]?.id;
      return id ? [id] : [];
    }
    return desktopSelectedIds;
  }, [isWide, mobileResourceId, desktopSelectedIds, resources]);

  /** Turnos del día mostrado — items puede traer el rango semana/mes. */
  const itemsForDay = useMemo(
    () => items.filter((a) => isSameLocalDay(new Date(a.startsAt), day)),
    [items, day],
  );

  const dayKpis = useMemo(
    () =>
      computeDayBoardKpis({
        appointments: itemsForDay,
        resources: visibleResources,
        timeOffs,
        day,
      }),
    [itemsForDay, visibleResources, timeOffs, day],
  );

  const serviceById = useMemo(() => {
    const map = new Map<string, ServiceProductOption>();
    for (const s of services) map.set(s.id, s);
    return map;
  }, [services]);

  /** PELU-09: match por entrada — solo sobre el rango ya cargado (day→hoy, week→la semana). */
  const waitlistMatches = useMemo(() => {
    const out = new Map<string, WaitlistEntryMatch>();
    if (waitlist.length === 0) return out;
    // Días con datos en memoria desde el día visto (cap 14 por perf).
    const daysAhead = Math.max(
      1,
      Math.min(14, Math.ceil((range.to.getTime() - startOfLocalDay(day).getTime()) / 86_400_000)),
    );
    for (const e of waitlist) {
      if (e.status !== 'waiting') continue;
      const m = waitlistEntryMatch({
        entry: e,
        resources,
        appointments: items,
        timeOffs,
        durationMinutes: serviceById.get(e.productId)?.durationMinutes ?? 0,
        fromDay: day,
        daysAhead,
      });
      if (m) out.set(e.id, m);
    }
    return out;
  }, [waitlist, resources, items, timeOffs, serviceById, day, range.to]);

  /** Banner del board: por columna, count de entradas cuyo mejor hueco cae hoy ahí. */
  const waitlistBanners = useMemo(() => {
    const out = new Map<string, { startMinutes: number; count: number }>();
    for (const e of waitlist) {
      if (e.status !== 'waiting') continue;
      const m = waitlistMatches.get(e.id);
      if (!m || !isSameLocalDay(m.startsAt, day)) continue;
      const startMinutes = m.startsAt.getHours() * 60 + m.startsAt.getMinutes();
      const cur = out.get(m.resourceId);
      if (cur) {
        cur.count += 1;
        cur.startMinutes = Math.min(cur.startMinutes, startMinutes);
      } else {
        out.set(m.resourceId, { startMinutes, count: 1 });
      }
    }
    return out;
  }, [waitlist, waitlistMatches, day]);

  const waitlistEnabled = bookingSettings?.bookingWaitlistEnabled === true;
  const waitlistActiveCount = waitlist.filter(
    (e) => e.status === 'waiting' || e.status === 'offered',
  ).length;

  const onOfferWaitlist = async (e: AppointmentWaitlistEntry, m: WaitlistEntryMatch) => {
    if (!canEdit || offerBusyId) return;
    setOfferBusyId(e.id);
    try {
      await offerWaitlistEntry({
        entryId: e.id,
        resourceId: m.resourceId,
        startsAt: m.startsAt.toISOString(),
        endsAt: m.endsAt.toISOString(),
      });
      showSuccess('Turno ofrecido — el cliente lo acepta desde el portal.');
      await load({ silent: true });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'No se pudo ofrecer el turno.');
    } finally {
      setOfferBusyId(null);
    }
  };

  const handleArrived = async (a: Appointment) => {
    try {
      await markAppointmentArrived(a.id);
      await load({ silent: true });
    } catch (e) {
      presentUnexpectedFailure({ module: 'appointments', op: 'agenda', error: e, title: 'Agenda', userMessage: 'No se pudo marcar llegada.' });
    }
  };

  const handleClearArrived = async (a: Appointment) => {
    try {
      await clearAppointmentArrived(a.id);
      await load({ silent: true });
    } catch (e) {
      presentUnexpectedFailure({ module: 'appointments', op: 'agenda', error: e, title: 'Agenda', userMessage: 'No se pudo deshacer la llegada.' });
    }
  };

  const moreActions = useMemo<TicketMoreAction[]>(() => {
    const actions: TicketMoreAction[] = [];
    if (waitlistEnabled || waitlistActiveCount > 0) {
      actions.push({
        key: 'waitlist',
        label: `Lista de espera${waitlistActiveCount > 0 ? ` (${waitlistActiveCount})` : ''}`,
        icon: 'people-outline',
        onPress: () => {
          setWaitlistSlotFilter(null);
          setWaitlistOpen(true);
        },
        testID: 'appointments.waitlist',
      });
    }
    if (canManageAgenda) {
      actions.push({
        key: 'configure',
        label: 'Configurar agendas',
        icon: 'settings-outline',
        onPress: () => navigateInShell(navigation, 'AppointmentResources'),
        testID: 'appointments.configure',
      });
    }
    if (hasServicePackages) {
      actions.push({
        key: 'packages',
        label: 'Paquetes',
        icon: 'cube-outline',
        onPress: () => setPackagesOpen(true),
        testID: 'appointments.packages',
      });
    }
    return actions;
  }, [canManageAgenda, hasServicePackages, navigation, waitlistActiveCount, waitlistEnabled]);

  const dateLabel =
    viewMode === 'week' ? formatWeekLabel(day) : viewMode === 'month' ? formatMonthLabel(day) : formatDayLabel(day);

  const shiftDate = (dir: -1 | 1) => {
    if (viewMode === 'week') setDay((d) => startOfLocalDay(addDays(d, dir * 7)));
    else if (viewMode === 'month') setDay((d) => startOfLocalDay(addMonths(d, dir)));
    else setDay((d) => startOfLocalDay(addDays(d, dir)));
  };

  const navUnit = viewMode === 'week' ? 'Semana' : viewMode === 'month' ? 'Mes' : 'Día';

  useEffect(() => {
    if (!isWide || resources.length === 0) return;
    setDesktopSelectedIds((prev) => reconcileDesktopResourceIds(prev, resources, day.getDay()));
  }, [day, isWide, resources]);

  if (!hasAppointments) {
    return <ModuleEmptyState title="Agenda no disponible" subtitle="Este rubro no tiene Agenda." testID="appointments.unavailable" />;
  }
  if (!canView) {
    return <ModuleEmptyState title="Agenda no disponible" subtitle="Pedile al dueño que te habilite Agenda." testID="appointments.unavailable" />;
  }

  return (
    <ShellContentFrame style={styles.container}>
      {resources.length === 0 && !loading ? (
        <View style={styles.setupWrap}>
          <ModuleEmptyState
            testID={noLinkedResource ? 'appointments.no-linked-resource' : 'appointments.no-agendas'}
            title={noLinkedResource ? 'Sin agenda vinculada' : 'Creá tu primera agenda'}
            subtitle={
              noLinkedResource
                ? 'Tu usuario no tiene agenda en esta sucursal. Pedile al dueño que te asocie.'
                : 'Una agenda por profesional o cabina. Sin eso no hay turnos.'
            }
            primaryAction={
              noLinkedResource || !canManageAgenda
                ? undefined
                : {
                    label: 'Crear agendas',
                    onPress: () => navigateInShell(navigation, 'AppointmentResources'),
                  }
            }
          />
        </View>
      ) : (
        <>
          <View style={[styles.toolbar, !isWide && styles.toolbarMobile]}>
            {isWide ? (
              <>
                <View style={styles.dateNav}>
                  <TouchableOpacity
                    onPress={() => shiftDate(-1)}
                    hitSlop={6}
                    style={[styles.dateNavBtn, Platform.OS === 'web' && styles.webReset]}
                    accessibilityRole="button"
                    accessibilityLabel={`${navUnit} anterior`}
                    testID="appointments.date.prev"
                  >
                    <Text style={styles.dateNavBtnText}>‹</Text>
                  </TouchableOpacity>
                  <Text style={styles.dateNavLabel}>{dateLabel}</Text>
                  <TouchableOpacity
                    onPress={() => shiftDate(1)}
                    hitSlop={6}
                    style={[styles.dateNavBtn, Platform.OS === 'web' && styles.webReset]}
                    accessibilityRole="button"
                    accessibilityLabel={`${navUnit} siguiente`}
                    testID="appointments.date.next"
                  >
                    <Text style={styles.dateNavBtnText}>›</Text>
                  </TouchableOpacity>
                </View>
                <View style={styles.toolbarActions}>
                  <View style={styles.viewToggle}>
                    {(['day', 'week', 'month'] as AgendaViewMode[]).map((mode, i) => (
                      <TouchableOpacity
                        key={mode}
                        style={[
                          styles.viewBtn,
                          i > 0 && styles.viewBtnDivider,
                          viewMode === mode && styles.viewBtnActive,
                          Platform.OS === 'web' && styles.webReset,
                        ]}
                        onPress={() => setViewMode(mode)}
                        accessibilityRole="button"
                        accessibilityLabel={`Vista ${mode === 'day' ? 'día' : mode === 'week' ? 'semana' : 'mes'}`}
                        testID={`appointments.view.${mode}`}
                      >
                        <Text style={[styles.viewBtnText, viewMode === mode && styles.viewBtnTextActive]}>
                          {mode === 'day' ? 'Día' : mode === 'week' ? 'Semana' : 'Mes'}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                  {viewMode === 'day' ? (
                    <View style={styles.viewToggle}>
                      {(['board', 'list'] as const).map((mode, i) => (
                        <TouchableOpacity
                          key={mode}
                          style={[
                            styles.viewBtn,
                            i > 0 && styles.viewBtnDivider,
                            dayMode === mode && styles.viewBtnActive,
                            Platform.OS === 'web' && styles.webReset,
                          ]}
                          onPress={() => setDayMode(mode)}
                          accessibilityRole="button"
                          accessibilityLabel={`Vista ${mode === 'board' ? 'tablero' : 'lista'}`}
                          testID={`appointments.dayMode.${mode}`}
                        >
                          <Text style={[styles.viewBtnText, dayMode === mode && styles.viewBtnTextActive]}>
                            {mode === 'board' ? 'Tablero' : 'Lista'}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  ) : null}
                  <TouchableOpacity
                    style={[styles.btn, hoyDisabled && styles.btnMuted, Platform.OS === 'web' && styles.webReset]}
                    onPress={() => setDay(today)}
                    disabled={hoyDisabled}
                    accessibilityRole="button"
                    accessibilityLabel="Hoy"
                    testID="appointments.today"
                  >
                    <Text style={[styles.btnText, hoyDisabled && styles.btnTextMuted]}>
                      Hoy
                    </Text>
                  </TouchableOpacity>
                  {(waitlistEnabled || waitlistActiveCount > 0) && viewMode !== 'day' ? (
                    <TouchableOpacity
                      style={[styles.btnSecondary, styles.btnWaitlist, Platform.OS === 'web' && styles.webReset]}
                      onPress={() => {
                        setWaitlistSlotFilter(null);
                        setWaitlistOpen(true);
                      }}
                      accessibilityRole="button"
                      accessibilityLabel={`En espera: ${waitlistActiveCount} en lista de espera`}
                      testID="appointments.waitlist"
                    >
                      <Text style={styles.btnSecondaryText}>En espera</Text>
                      <View style={styles.waitlistBadge}>
                        <Text style={styles.waitlistBadgeText}>{waitlistActiveCount}</Text>
                      </View>
                    </TouchableOpacity>
                  ) : null}
                  {moreActions.length > 0 ? (
                    <TouchableOpacity
                      style={[styles.btnSecondary, Platform.OS === 'web' && styles.webReset]}
                      onPress={() => setMoreOpen(true)}
                      accessibilityRole="button"
                      accessibilityLabel="Más opciones"
                      testID="appointments.more"
                    >
                      <Text style={styles.btnSecondaryText}>Más ▾</Text>
                    </TouchableOpacity>
                  ) : null}
                  <TouchableOpacity
                    style={[styles.btnPrimary, Platform.OS === 'web' && styles.webReset]}
                    onPress={() => openCreate()}
                    disabled={!canEdit}
                    accessibilityRole="button"
                    accessibilityLabel="Nuevo turno"
                    testID="appointments.new"
                  >
                    <Text style={styles.btnPrimaryText}>Nuevo turno</Text>
                  </TouchableOpacity>
                </View>
              </>
            ) : (
              <>
                <View style={styles.toolbarDateRow}>
                  <View style={[styles.dateNav, styles.dateNavMobile]}>
                    <TouchableOpacity
                      onPress={() => shiftDate(-1)}
                      hitSlop={6}
                      style={[styles.dateNavBtn, Platform.OS === 'web' && styles.webReset]}
                      accessibilityRole="button"
                      accessibilityLabel={`${navUnit} anterior`}
                    >
                      <Text style={styles.dateNavBtnText}>‹</Text>
                    </TouchableOpacity>
                    <Text style={styles.dateNavLabelMobile} numberOfLines={1}>
                      {dateLabel}
                    </Text>
                    <TouchableOpacity
                      onPress={() => shiftDate(1)}
                      hitSlop={6}
                      style={[styles.dateNavBtn, Platform.OS === 'web' && styles.webReset]}
                      accessibilityRole="button"
                      accessibilityLabel={`${navUnit} siguiente`}
                    >
                      <Text style={styles.dateNavBtnText}>›</Text>
                    </TouchableOpacity>
                  </View>
                  <TouchableOpacity
                    style={[styles.btn, hoyDisabled && styles.btnMuted, Platform.OS === 'web' && styles.webReset]}
                    onPress={() => setDay(today)}
                    disabled={hoyDisabled}
                    accessibilityRole="button"
                    accessibilityLabel="Hoy"
                    testID="appointments.today"
                  >
                    <Text style={[styles.btnText, hoyDisabled && styles.btnTextMuted]}>
                      Hoy
                    </Text>
                  </TouchableOpacity>
                  {moreActions.length > 0 ? (
                    <TouchableOpacity
                      style={[styles.btn, Platform.OS === 'web' && styles.webReset]}
                      onPress={() => setMoreOpen(true)}
                      accessibilityRole="button"
                      accessibilityLabel="Más opciones"
                      testID="appointments.more"
                    >
                      <Text style={styles.btnText}>Más</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
                <View style={styles.toolbarActionsMobile}>
                  <View style={[styles.viewToggle, styles.viewToggleMobile]}>
                    {(['day', 'week', 'month'] as AgendaViewMode[]).map((mode, i) => (
                      <TouchableOpacity
                        key={mode}
                        style={[
                          styles.viewBtn,
                          styles.viewBtnMobile,
                          i > 0 && styles.viewBtnDivider,
                          viewMode === mode && styles.viewBtnActive,
                          Platform.OS === 'web' && styles.webReset,
                        ]}
                        onPress={() => setViewMode(mode)}
                        accessibilityRole="button"
                        accessibilityLabel={`Vista ${mode === 'day' ? 'día' : mode === 'week' ? 'semana' : 'mes'}`}
                        testID={`appointments.view.${mode}`}
                      >
                        <Text style={[styles.viewBtnText, viewMode === mode && styles.viewBtnTextActive]}>
                          {mode === 'day' ? 'Día' : mode === 'week' ? 'Semana' : 'Mes'}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                  <TouchableOpacity
                    style={[styles.btnPrimary, Platform.OS === 'web' && styles.webReset]}
                    onPress={() => openCreate()}
                    disabled={!canEdit}
                    accessibilityRole="button"
                    accessibilityLabel="Nuevo turno"
                    testID="appointments.new"
                  >
                    <Text style={styles.btnPrimaryText}>Nuevo turno</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>

          {isWide && viewMode === 'day' && resources.length > DESKTOP_MAX_COLS ? (
            <View style={styles.callout}>
              <Text style={styles.calloutText}>
                <Text style={styles.calloutStrong}>{resources.length} agendas:</Text>
                {' '}se ven como máximo {DESKTOP_MAX_COLS}. Usá{' '}
                <Text style={styles.calloutStrong}>Elegir agendas</Text>
                {' '}para marcar cuáles (1–{DESKTOP_MAX_COLS}).
              </Text>
            </View>
          ) : null}

          {isWide && viewMode === 'day' ? (
            <AgendaDayKpis
              kpis={dayKpis}
              waitlistCount={
                waitlistEnabled || waitlistActiveCount > 0 ? waitlistActiveCount : null
              }
              onPressWaitlist={() => {
                setWaitlistSlotFilter(null);
                setWaitlistOpen(true);
              }}
            />
          ) : null}

          <AgendaVisibleBar
            isWide={isWide}
            resources={resources}
            desktopSelectedIds={desktopSelectedIds}
            mobileResourceId={mobileResourceId}
            onMobileResourceChange={setMobileResourceId}
            onPressChoose={() => setPickerOpen(true)}
            summary={
              viewMode === 'day'
                ? `${dayKpis.total} turno${dayKpis.total === 1 ? '' : 's'}` +
                  (waitlistActiveCount > 0 ? ` · ${waitlistActiveCount} en espera` : '')
                : undefined
            }
          />

          {viewMode === 'day' && dayMode === 'board' && !moving && resources.length > 0 ? (
            <Text style={styles.boardHint}>
              Tocá un hueco libre para crear un turno · mantené apretado un turno para reagendarlo
            </Text>
          ) : null}

          {moving ? (
            <View style={styles.moveBar}>
              <Text style={styles.moveBarText} numberOfLines={2}>
                Reagendando{' '}
                <Text style={styles.moveBarStrong}>
                  {moving.customerName}
                  {moving.items?.[0]?.nameSnapshot
                    ? ` · ${moving.items[0].nameSnapshot}`
                    : productNameById.get(moving.productId)
                      ? ` · ${productNameById.get(moving.productId)}`
                      : ''}
                </Text>{' '}
                — tocá un hueco destino (verde = entra completo)
              </Text>
              <TouchableOpacity
                style={styles.moveBarCancel}
                onPress={() => {
                  setMoving(null);
                  setMoveConfirm(null);
                  setMovePreview(null);
                }}
                accessibilityRole="button"
                accessibilityLabel="Salir del modo reagendar"
                testID="appointments.move.exit"
              >
                <Text style={styles.moveBarCancelText}>Salir ✕</Text>
              </TouchableOpacity>
            </View>
          ) : null}

          <View style={styles.boardScroll}>
            {viewMode === 'day' ? (
              isWide && dayMode === 'list' ? (
                <AppointmentsDayList
                  day={day}
                  appointments={itemsForDay}
                  resources={resources}
                  productNameById={productNameById}
                  showCancelled={showCancelled}
                  canEdit={canEdit}
                  selectedAppointmentId={selected?.id ?? null}
                  refreshing={loading}
                  onRefresh={() => void load()}
                  onPressAppointment={setSelected}
                  onConfirm={(a) => void onStatus(a.id, 'confirmed')}
                  onArrive={(a) => void handleArrived(a)}
                />
              ) : (
                <AppointmentsDayBoard
                  day={day}
                  isWide={isWide}
                  visibleResources={visibleResources}
                  allResources={resources}
                  appointments={items}
                  timeOffs={timeOffs}
                  showCancelled={showCancelled}
                  productNameById={productNameById}
                  quoteSessionsTotalById={quoteSessionsTotalById}
                  onPressSlot={onPressSlot}
                  onPressAppointment={setSelected}
                  onLongPressAppointment={startMove}
                  moving={
                    moving
                      ? {
                          appointmentId: moving.id,
                          resourceId: moving.resourceId,
                          durationMinutes: durationMinutes(moving.startsAt, moving.endsAt),
                        }
                      : null
                  }
                  movePreview={movePreview}
                  onPressMoveTarget={onPressMoveTarget}
                  waitlistBanners={waitlistBanners}
                  onPressWaitlistBanner={(resourceId) => {
                    const banner = waitlistBanners.get(resourceId);
                    setWaitlistSlotFilter(
                      banner ? { resourceId, startMinutes: banner.startMinutes } : null,
                    );
                    setWaitlistOpen(true);
                  }}
                  selectedAppointmentId={selected?.id ?? null}
                  refreshing={loading}
                  onRefresh={() => void load()}
                />
              )
            ) : viewMode === 'week' ? (
              <AppointmentsWeekBoard
                anchorDay={day}
                isWide={isWide}
                resourceIds={boardResourceIds}
                resourceColorById={resourceColorById}
                resourceNameById={resourceNameById}
                appointments={items}
                timeOffs={timeOffs}
                productNameById={productNameById}
                onSelectDay={(d) => {
                  setDay(startOfLocalDay(d));
                  setViewMode('day');
                }}
                onCreateAtDay={canEdit ? (d) => openCreate({ day: d }) : undefined}
                onPressAppointment={setSelected}
                refreshing={loading}
                onRefresh={() => void load()}
              />
            ) : (
              <AppointmentsMonthBoard
                anchorDay={day}
                isWide={isWide}
                resourceIds={boardResourceIds}
                appointments={items}
                onSelectDay={(d) => {
                  setDay(startOfLocalDay(d));
                  setViewMode('day');
                }}
                refreshing={loading}
                onRefresh={() => void load()}
              />
            )}
          </View>

          <TicketMoreActionsSheet
            visible={moreOpen}
            title="Más opciones"
            actions={moreActions}
            onClose={() => setMoreOpen(false)}
          />

          <AgendaStaffPickerModal
            visible={pickerOpen}
            resources={resources}
            selectedIds={desktopSelectedIds}
            dayOfWeek={dayOfWeek}
            showCancelled={showCancelled}
            onShowCancelledChange={setShowCancelled}
            onClose={() => setPickerOpen(false)}
            onApply={(ids) => {
              setDesktopSelectedIds(ids.slice(0, DESKTOP_MAX_COLS));
              setPickerOpen(false);
            }}
          />

          <AppointmentWaitlistPanel
            visible={waitlistOpen}
            day={day}
            entries={waitlist}
            matches={waitlistMatches}
            resourceNameById={resourceNameById}
            resourceColorById={resourceColorById}
            productNameById={productNameById}
            offerMinutes={bookingSettings?.bookingWaitlistOfferMinutes ?? 15}
            slotFilter={waitlistSlotFilter}
            onClearSlotFilter={() => setWaitlistSlotFilter(null)}
            canEdit={canEdit}
            offerBusyId={offerBusyId}
            onOffer={(e, m) => void onOfferWaitlist(e, m)}
            onClose={() => setWaitlistOpen(false)}
          />

          <AppointmentActionsSheet
            appointment={selected}
            resourceName={selected ? resourceNameById.get(selected.resourceId) : undefined}
            serviceName={
              selected
                ? selected.items && selected.items.length > 0
                  ? selected.items.length === 1
                    ? selected.items[0]!.nameSnapshot
                    : `${selected.items[0]!.nameSnapshot} + ${selected.items.length - 1} más`
                  : productNameById.get(selected.productId)
                : undefined
            }
            storeName={branchName}
            isWide={isWide}
            canEdit={canEdit}
            canWorkOrder={hasWorkOrders}
            onClose={() => setSelected(null)}
            onOpenQuote={(quoteId) => {
              setSelected(null);
              navigateInShell(navigation, 'QuoteDetail', { quoteId });
            }}
            onOpenSale={(saleId) => setDetailSaleId(saleId)}
            onOpenCustomer={(customerId) => {
              setSelected(null);
              openCustomerDetail(navigation, customerId);
            }}
            onStatus={onStatus}
            onEdit={openEdit}
            onMove={startMove}
            onArrived={(a) => void handleArrived(a)}
            onClearArrived={(a) => void handleClearArrived(a)}
            onCharge={(a) => {
              const ids =
                a.items && a.items.length > 0
                  ? a.items.map((i) => i.productId)
                  : a.productId
                    ? [a.productId]
                    : [];
              if (ids.length === 0) {
                showError('Agenda', 'Este turno no tiene un servicio asociado para cobrar.');
                return;
              }
              // Bridge before navigate: POS also re-sets after fetch; this covers race if sale is fast.
              setPendingAppointmentCheckout(a.id, ids[0], ids);
              setSelected(null);
              navigateInShell(navigation, 'POS', { appointmentId: a.id });
            }}
            onWorkOrder={async (a) => {
              try {
                const existingId = await findWorkOrderByAppointment(a.id);
                setSelected(null);
                if (existingId) {
                  navigateInShell(navigation, 'WorkOrderDetail', { workOrderId: existingId });
                  return;
                }
                navigateInShell(navigation, 'WorkOrderForm', {
                  appointmentId: a.id,
                  customerName: a.customerName,
                  customerId: a.customerId ?? null,
                });
              } catch (e) {
                presentUnexpectedFailure({ module: 'appointments', op: 'ot', error: e, title: 'OT', userMessage: 'No se pudo abrir la OT.' });
              }
            }}
          />

          <SaleDetailModal
            visible={detailSaleId != null}
            saleId={detailSaleId}
            branchId={branchId ?? null}
            onClose={() => setDetailSaleId(null)}
          />

          <OperationalConfirmModal
            visible={moveConfirm != null}
            title="Reagendar turno"
            message={
              moveConfirm && moving
                ? `${moving.customerName} pasa a ${formatDayLabel(day)} ${minutesToHm(moveConfirm.startMinutes)} — ${resourceNameById.get(moveConfirm.resourceId) ?? 'otra agenda'}`
                : ''
            }
            confirmLabel="Reagendar"
            loading={moveBusy}
            onClose={() => {
              if (!moveBusy) setMoveConfirm(null);
            }}
            onConfirm={() => {
              if (moveConfirm) void doMove(moveConfirm);
            }}
          />

          <AppointmentFormModal
            visible={formOpen}
            mode={formMode}
            editing={editingAppointment}
            orgId={orgId}
            branchId={branchId}
            resources={resources}
            timeOffs={timeOffs}
            appointments={items}
            day={day}
            prefill={formPrefill}
            isWide={isWide}
            onClose={() => {
              setFormOpen(false);
              setFormPrefill(null);
              setEditingAppointment(null);
            }}
            onSaved={() => {
              setFormOpen(false);
              setFormPrefill(null);
              setEditingAppointment(null);
              void load();
            }}
          />

          <ServicePackagesModal
            visible={packagesOpen}
            orgId={orgId}
            branchId={branchId}
            isWide={isWide}
            onClose={() => setPackagesOpen(false)}
          />
        </>
      )}
    </ShellContentFrame>
  );
}

function ResponsiveFormShell(props: {
  visible: boolean;
  isWide: boolean;
  /** Proto .tmh .kicker — eyebrow "AGENDA · <recurso>" sobre el título. */
  kicker?: string;
  title: string;
  /** Proto pill inline junto al h2 (estado del turno). */
  titleTrailing?: React.ReactNode;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Confirm overlay (same Modal) — evita segundo Modal nativo. */
  overlay?: React.ReactNode;
  /** Ancho del diálogo desktop (proto .modal.wide = 700). */
  maxWidth?: number;
}) {
  const { height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  if (!props.visible) return null;

  // Modals can report insets.bottom=0; keep a real gap above the home indicator / nav bar.
  const bottomPad = props.isWide ? spacing.sm : Math.max(insets.bottom, 24);
  const sheetMax = Math.round(height * (props.isWide ? 0.9 : 0.88));
  const headerReserve = 56 + (props.kicker ? 16 : 0) + (props.subtitle ? 22 : 0);
  const footerReserve = props.footer ? 64 + bottomPad : 0;
  const bodyMax = Math.max(120, sheetMax - headerReserve - footerReserve);
  // Proto: desktop .tmh 16/20/14 + .modal-body 16/20 · mobile 12/16/10 + body 12/16.
  const padX = props.isWide ? 20 : 16;

  return (
    <Modal
      visible
      animationType={props.isWide ? 'fade' : 'slide'}
      transparent
      onRequestClose={props.onClose}
      statusBarTranslucent
    >
      <View
        style={[
          styles.modalBackdrop,
          props.isWide ? styles.modalBackdropWide : styles.modalBackdropMobile,
        ]}
      >
        <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={props.onClose} />
        <View
          style={[
            props.isWide ? styles.dialogCard : styles.sheetCard,
            {
              maxHeight: sheetMax,
              ...(props.isWide
                ? { width: '100%' as const, maxWidth: props.maxWidth ?? CONTENT_MAX_WIDTH_FORM }
                : null),
            },
          ]}
        >
          {!props.isWide ? <View style={styles.grabber} /> : null}
          <View
            style={[
              styles.formHeader,
              { paddingHorizontal: padX, paddingBottom: props.isWide ? 14 : 10 },
            ]}
          >
            <View style={{ flex: 1, minWidth: 0 }}>
              {props.kicker ? <Text style={styles.modalKicker}>{props.kicker}</Text> : null}
              <View style={styles.formTitleRow}>
                <Text style={[styles.modalTitle, !props.isWide && styles.modalTitleMobile]}>
                  {props.title}
                </Text>
                {props.titleTrailing}
              </View>
              {props.subtitle ? <Text style={styles.modalSubtitle}>{props.subtitle}</Text> : null}
            </View>
            <TouchableOpacity
              onPress={props.onClose}
              hitSlop={10}
              accessibilityLabel="Cerrar"
              accessibilityRole="button"
              style={styles.closeBox}
            >
              <Text style={styles.closeGlyph}>✕</Text>
            </TouchableOpacity>
          </View>

          <View style={[styles.formBodyWrap, { maxHeight: bodyMax }]}>
            <ScrollView
              style={styles.formScroll}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
              contentContainerStyle={[
                styles.formScrollContent,
                { paddingHorizontal: padX, paddingTop: props.isWide ? 16 : 12 },
              ]}
              bounces={false}
              nestedScrollEnabled
            >
              {props.children}
            </ScrollView>
          </View>

          {props.footer ? (
            <View
              style={[
                styles.formFooter,
                { paddingHorizontal: padX, paddingTop: props.isWide ? 14 : 10, paddingBottom: bottomPad },
              ]}
            >
              {props.footer}
            </View>
          ) : null}
        </View>
        {props.overlay}
      </View>
    </Modal>
  );
}

function durationMinutes(startsAt: string, endsAt: string): number {
  const ms = new Date(endsAt).getTime() - new Date(startsAt).getTime();
  return Math.max(1, Math.round(ms / 60_000));
}

function statusChipColors(status: AppointmentStatus): { bg: string; border: string; text: string } {
  switch (status) {
    case 'requested':
      return agenda.requested;
    case 'confirmed':
      return agenda.confirmed;
    case 'completed':
      return agenda.completed;
    case 'cancelled':
      return agenda.cancelled;
    case 'no_show':
      return agenda.noShow;
    default:
      return agenda.cancelled;
  }
}

function AppointmentActionsSheet(props: {
  appointment: Appointment | null;
  resourceName?: string;
  serviceName?: string;
  storeName?: string;
  isWide: boolean;
  canEdit: boolean;
  /** Capability work_orders — sin ella no se muestra la acción OT (proto: "OT por capability"). */
  canWorkOrder: boolean;
  onClose: () => void;
  onOpenQuote: (quoteId: string) => void;
  onOpenSale: (saleId: string) => void;
  onOpenCustomer: (customerId: string) => void;
  onStatus: (id: string, status: AppointmentStatus) => void | Promise<void>;
  onEdit: (appointment: Appointment) => void;
  /** Modo mover: entra al board en modo target (I4). */
  onMove: (appointment: Appointment) => void;
  onArrived: (appointment: Appointment) => void;
  onClearArrived: (appointment: Appointment) => void;
  onCharge: (appointment: Appointment) => void;
  onWorkOrder: (appointment: Appointment) => void | Promise<void>;
}) {
  const item = props.appointment;
  const [moreOpen, setMoreOpen] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [workOrderBusy, setWorkOrderBusy] = useState(false);
  const [linkedWorkOrderId, setLinkedWorkOrderId] = useState<string | null | undefined>(undefined);
  const [quoteSessionsTotal, setQuoteSessionsTotal] = useState<number | null>(null);
  const [customerStats, setCustomerStats] = useState<{ visits: number; noShows: number } | null>(null);
  const [bookingSettings, setBookingSettings] = useState<BookingSettings | null>(null);
  const [reminderSentAt, setReminderSentAt] = useState<string | null>(null);
  const [pendingStatus, setPendingStatus] = useState<{
    status: AppointmentStatus;
    title: string;
    message: string;
    confirmLabel: string;
    destructive?: boolean;
    iconName: 'close-circle-outline' | 'person-remove-outline' | 'checkmark-circle-outline';
  } | null>(null);

  useEffect(() => {
    setMoreOpen(false);
    setPendingStatus(null);
    setStatusBusy(false);
    setWorkOrderBusy(false);
    setLinkedWorkOrderId(undefined);
    setQuoteSessionsTotal(null);
    setCustomerStats(null);
    setBookingSettings(null);
    setReminderSentAt(null);
  }, [item?.id]);

  useEffect(() => {
    if (!item?.id || !props.canWorkOrder) return;
    let cancelled = false;
    void findWorkOrderByAppointment(item.id)
      .then((id) => {
        if (!cancelled) setLinkedWorkOrderId(id);
      })
      .catch(() => {
        if (!cancelled) setLinkedWorkOrderId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [item?.id]);

  useEffect(() => {
    const quoteId = item?.quoteRequestId;
    if (!quoteId) return;
    let cancelled = false;
    void getQuoteEstimatedSessions(quoteId)
      .then((n) => {
        if (!cancelled) setQuoteSessionsTotal(n);
      })
      .catch(() => {
        if (!cancelled) setQuoteSessionsTotal(null);
      });
    return () => {
      cancelled = true;
    };
  }, [item?.quoteRequestId, item?.id]);

  useEffect(() => {
    if (!item?.id) return;
    let cancelled = false;
    void getAppointmentReminderSentAt(item.id)
      .then((iso) => {
        if (!cancelled) setReminderSentAt(iso);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [item?.id]);

  useEffect(() => {
    const customerId = item?.customerId;
    if (!customerId) return;
    let cancelled = false;
    void getCustomerAppointmentStats(customerId)
      .then((stats) => {
        if (!cancelled) setCustomerStats(stats);
      })
      .catch(() => {
        if (!cancelled) setCustomerStats(null);
      });
    return () => {
      cancelled = true;
    };
  }, [item?.customerId, item?.id]);

  useEffect(() => {
    if (item?.paymentStatus !== 'deposit_paid') return;
    let cancelled = false;
    void getBookingSettings(item.branchId)
      .then((s) => {
        if (!cancelled) setBookingSettings(s);
      })
      .catch(() => {
        if (!cancelled) setBookingSettings(null);
      });
    return () => {
      cancelled = true;
    };
  }, [item?.paymentStatus, item?.branchId, item?.id]);

  if (!item) return null;

  const canAct = props.canEdit && (item.status === 'requested' || item.status === 'confirmed');
  const chip = statusChipColors(item.status);
  const mins = durationMinutes(item.startsAt, item.endsAt);
  const sourceLabel = item.source === 'app' ? 'Mostrador' : 'Online';
  const phone = item.customerPhone?.trim() || null;
  const quoteOriginLabel =
    item.quoteRequestId != null
      ? formatQuoteSessionMeta(item.sessionIndex, quoteSessionsTotal)
      : null;

  const amountTotal = item.amountTotal ?? null;
  const amountPaid = item.amountPaid ?? null;
  const amountRest =
    item.status !== 'cancelled' &&
    amountTotal != null &&
    amountPaid != null &&
    amountTotal - amountPaid > 0.5
      ? amountTotal - amountPaid
      : null;
  const amountCredit =
    amountTotal != null && amountPaid != null && amountPaid - amountTotal > 0.5
      ? amountPaid - amountTotal
      : null;
  // Ya está saldado (por status o por montos): no ofrecer otro cobro.
  const fullyPaid =
    item.paymentStatus === 'paid' ||
    (amountTotal != null && amountPaid != null && amountPaid >= amountTotal - 0.5);
  const canCharge = props.canEdit && canAct && !item.saleId && !fullyPaid;
  const hasMoney = amountTotal != null || amountPaid != null;

  const depositResolution = item.depositResolutionStatus ?? null;
  const depositResolutionAmount = item.depositResolutionAmount ?? amountPaid;
  const depositResolutionBox =
    depositResolution != null && depositResolution !== 'not_applicable'
      ? depositResolution === 'kept'
        ? {
            tone: 'warn' as const,
            text: `Seña retenida: ${formatMoney(depositResolutionAmount ?? 0)} — política ${
              item.status === 'no_show' ? 'de no-show' : 'de cancelación'
            } de la sucursal.`,
          }
        : depositResolution === 'refund_pending'
          ? {
              tone: 'warn' as const,
              text: `Devolución de la seña pendiente (${formatMoney(depositResolutionAmount ?? 0)}).`,
            }
          : depositResolution === 'refunded'
            ? {
                tone: 'ok' as const,
                text: `Seña devuelta: ${formatMoney(depositResolutionAmount ?? 0)}.`,
              }
            : {
                tone: 'bad' as const,
                text: `No se pudo devolver la seña (${formatMoney(depositResolutionAmount ?? 0)}).`,
              }
      : null;

  const pendingDepositPolicy =
    canAct && item.paymentStatus === 'deposit_paid' && amountPaid != null && bookingSettings
      ? bookingSettings.bookingNoShowPolicy === 'keep' &&
        bookingSettings.bookingLateCancelPolicy === 'keep'
        ? `Si no viene o cancela tarde, se retiene la seña (${formatMoney(amountPaid)}) — política de la sucursal.`
        : bookingSettings.bookingNoShowPolicy === 'refund' &&
            bookingSettings.bookingLateCancelPolicy === 'refund'
          ? `Si no viene o cancela, la seña se devuelve — política de la sucursal.`
          : `No-show: la seña se ${bookingSettings.bookingNoShowPolicy === 'keep' ? 'retiene' : 'devuelve'} · Cancelación tarde: se ${
              bookingSettings.bookingLateCancelPolicy === 'keep' ? 'retiene' : 'devuelve'
            } (${formatMoney(amountPaid)}).`
      : null;

  const requestStatus = (status: AppointmentStatus) => {
    if (status === 'cancelled') {
      setPendingStatus({
        status,
        title: 'Cancelar turno',
        message: 'El turno queda cancelado.',
        confirmLabel: 'Sí, cancelar',
        destructive: true,
        iconName: 'close-circle-outline',
      });
      return;
    }
    if (status === 'no_show') {
      setPendingStatus({
        status,
        title: 'No vino',
        message:
          item.paymentStatus === 'deposit_paid' || item.paymentStatus === 'paid'
            ? '¿Marcar que no vino? Se aplicará la regla de pago anticipado de la sucursal.'
            : '¿Marcar que no vino?',
        confirmLabel: 'Sí, marcar',
        destructive: true,
        iconName: 'person-remove-outline',
      });
      return;
    }
    if (status === 'completed') {
      setPendingStatus({
        status,
        title: 'Completar sin cobro',
        message: '¿Lo das por hecho sin cobrar en el POS? Las comisiones se calculan solo al cobrar.',
        confirmLabel: 'Completar',
        iconName: 'checkmark-circle-outline',
      });
      return;
    }
    void props.onStatus(item.id, status);
  };

  const confirmPendingStatus = async () => {
    if (!pendingStatus) return;
    setStatusBusy(true);
    try {
      await props.onStatus(item.id, pendingStatus.status);
      setPendingStatus(null);
    } finally {
      setStatusBusy(false);
    }
  };

  const metaItems: { key: string; label: string; value: React.ReactNode }[] = [
    { key: 'agenda', label: 'Agenda', value: props.resourceName ?? '—' },
    ...(props.serviceName
      ? [{ key: 'service', label: 'Servicio', value: props.serviceName }]
      : []),
    {
      key: 'origen',
      label: 'Origen',
      value:
        quoteOriginLabel && item.quoteRequestId ? (
          <TouchableOpacity
            onPress={() => props.onOpenQuote(item.quoteRequestId!)}
            accessibilityRole="link"
            accessibilityLabel={`Ver cotización · ${quoteOriginLabel}`}
            style={styles.metaQuoteHit}
            hitSlop={6}
          >
            <Text style={styles.metaLink} numberOfLines={1}>
              {quoteOriginLabel}
            </Text>
            <Text style={styles.metaQuoteChevron} accessible={false}>
              ›
            </Text>
          </TouchableOpacity>
        ) : (
          sourceLabel
        ),
    },
  ];
  if (phone) {
    const href = telHref(phone);
    metaItems.push({
      key: 'phone',
      label: 'Teléfono',
      value: href ? (
        <TouchableOpacity
          onPress={() => void Linking.openURL(href)}
          accessibilityRole="link"
          hitSlop={6}
        >
          <Text style={styles.metaLink}>{formatArPhoneDisplay(phone)}</Text>
        </TouchableOpacity>
      ) : (
        <Text style={styles.metaLink}>{formatArPhoneDisplay(phone)}</Text>
      ),
    });
  }
  if (item.customerEmail) {
    metaItems.push({
      key: 'email',
      label: 'Email',
      value: (
        <Text style={styles.metaV} numberOfLines={1}>
          {item.customerEmail}
        </Text>
      ),
    });
  }
  if (item.customerId) {
    const statsLabel = customerStats
      ? ` · ${customerStats.visits} ${customerStats.visits === 1 ? 'visita' : 'visitas'} · ${customerStats.noShows} no-shows`
      : '';
    metaItems.push({
      key: 'customer',
      label: 'Cliente',
      value: (
        <TouchableOpacity
          onPress={() => props.onOpenCustomer(item.customerId!)}
          accessibilityRole="link"
          accessibilityLabel={`Ver ficha de ${item.customerName}`}
          style={styles.metaQuoteHit}
          hitSlop={6}
        >
          <Text style={styles.metaLink} numberOfLines={1}>
            Ver ficha{statsLabel}
          </Text>
          <Text style={styles.metaQuoteChevron} accessible={false}>
            ›
          </Text>
        </TouchableOpacity>
      ),
    });
  }
  if (item.notes) {
    metaItems.push({
      key: 'notes',
      label: 'Nota',
      value: <Text style={styles.metaNote}>{item.notes}</Text>,
    });
  }
  if (reminderSentAt) {
    metaItems.push({
      key: 'reminder',
      label: 'Recordatorio',
      value: <Text style={styles.metaV}>WhatsApp enviado {formatShortDateTime(reminderSentAt)}</Text>,
    });
  }
  if (item.saleId) {
    metaItems.push({
      key: 'sale',
      label: 'Venta',
      value: (
        <TouchableOpacity
          onPress={() => props.onOpenSale(item.saleId!)}
          accessibilityRole="link"
          accessibilityLabel={`Ver venta ${item.saleId.slice(0, 8)}`}
          style={styles.metaQuoteHit}
          hitSlop={6}
        >
          <Text style={styles.metaLink} numberOfLines={1}>
            #{item.saleId.slice(0, 8)} · Ver venta
          </Text>
          <Text style={styles.metaQuoteChevron} accessible={false}>
            ›
          </Text>
        </TouchableOpacity>
      ),
    });
  }

  return (
    <ResponsiveFormShell
      visible
      isWide={props.isWide}
      kicker={`Agenda${props.resourceName ? ` · ${props.resourceName}` : ''}`}
      title={item.customerName}
      titleTrailing={
        <View style={[styles.statusPill, { backgroundColor: chip.bg, borderColor: chip.border }]}>
          <Text style={[styles.statusPillText, { color: chip.text }]}>
            {STATUS_LABELS[item.status]}
            {item.arrivedAt ? ` · ✓ Llegó ${formatTime(item.arrivedAt)}` : ''}
          </Text>
        </View>
      }
      subtitle={`${formatDayLabel(new Date(item.startsAt)).replace(/,\s*/g, ' ')} · ${formatTime(
        item.startsAt
      )}–${formatTime(item.endsAt)} · ${sourceLabel}`}
      maxWidth={700}
      onClose={props.onClose}
      overlay={
        <OperationalConfirmModal
          visible={pendingStatus != null}
          presentation="overlay"
          title={pendingStatus?.title ?? ''}
          message={pendingStatus?.message ?? ''}
          confirmLabel={pendingStatus?.confirmLabel ?? 'Confirmar'}
          destructive={pendingStatus?.destructive}
          iconName={pendingStatus?.iconName}
          loading={statusBusy}
          onClose={() => {
            if (statusBusy) return;
            setPendingStatus(null);
          }}
          onConfirm={() => void confirmPendingStatus()}
        />
      }
    >
      <View style={styles.statRow}>
        <View style={styles.statCell}>
          <Text style={styles.statV}>{mins} min</Text>
          <Text style={styles.statL}>Duración</Text>
        </View>
        {hasMoney
          ? (
              [
                amountTotal != null
                  ? { key: 'total', value: formatMoney(amountTotal), label: 'Total', ok: false }
                  : null,
                amountPaid != null && amountPaid > 0
                  ? {
                      key: 'paid',
                      value: formatMoney(amountPaid),
                      label: item.paymentStatus === 'deposit_paid' ? 'Seña pagada' : 'Pagado',
                      ok: true,
                    }
                  : null,
                amountRest != null
                  ? { key: 'rest', value: formatMoney(amountRest), label: 'Resta cobrar', ok: false }
                  : null,
                amountCredit != null
                  ? { key: 'credit', value: formatMoney(amountCredit), label: 'A favor', ok: true }
                  : null,
              ] as const
            )
              .filter((c): c is NonNullable<typeof c> => c != null)
              .map((c) => (
                <View key={c.key} style={[styles.statCell, styles.statCellBorder]}>
                  <Text style={[styles.statV, c.ok && styles.statVOk]}>{c.value}</Text>
                  <Text style={styles.statL}>{c.label}</Text>
                </View>
              ))
          : null}
      </View>

      {pendingDepositPolicy ? (
        <View style={[styles.noteBox, styles.noteBoxInfo]}>
          <Text style={[styles.noteBoxText, styles.noteBoxInfoText]}>{pendingDepositPolicy}</Text>
        </View>
      ) : null}
      {depositResolutionBox ? (
        <View
          style={[
            styles.noteBox,
            depositResolutionBox.tone === 'warn'
              ? styles.noteBoxWarn
              : depositResolutionBox.tone === 'ok'
                ? styles.noteBoxOk
                : styles.noteBoxBad,
          ]}
        >
          <Text
            style={[
              styles.noteBoxText,
              depositResolutionBox.tone === 'warn'
                ? styles.noteBoxWarnText
                : depositResolutionBox.tone === 'ok'
                  ? styles.noteBoxOkText
                  : styles.noteBoxBadText,
            ]}
          >
            {depositResolutionBox.text}
          </Text>
        </View>
      ) : null}

      <View style={styles.detailSec}>
        <Text style={styles.secTitle}>Detalle</Text>
        {metaItems.map((row, i) => (
          <View
            key={row.key}
            style={[styles.metaRow, i < metaItems.length - 1 ? styles.metaRowBorder : null]}
          >
            <Text style={styles.metaK}>{row.label}</Text>
            {typeof row.value === 'string' ? (
              <Text style={styles.metaV} numberOfLines={2}>
                {row.value}
              </Text>
            ) : (
              row.value
            )}
          </View>
        ))}
      </View>

      {canAct ? (
        <View style={styles.secDivided}>
          <Text style={styles.secTitle}>Acciones</Text>
          <View style={styles.actGrid}>
            {canCharge ? (
              <TouchableOpacity
                style={[styles.actBtn, styles.actBtnPri]}
                onPress={() => props.onCharge(item)}
                accessibilityRole="button"
                accessibilityLabel="Cobrar en POS"
              >
                <Text style={styles.actBtnPriText}>
                  Cobrar en POS{amountRest != null ? ` · ${formatMoney(amountRest)} restantes` : ''}
                </Text>
              </TouchableOpacity>
            ) : null}
            {!item.arrivedAt ? (
              <TouchableOpacity
                style={[styles.actBtn, styles.actBtnHalf]}
                onPress={() => props.onArrived(item)}
                accessibilityRole="button"
                accessibilityLabel="Llegó"
              >
                <Text style={styles.actBtnText}>Llegó</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[styles.actBtn, styles.actBtnHalf]}
                onPress={() => props.onClearArrived(item)}
                accessibilityRole="button"
                accessibilityLabel="Deshacer llegada"
              >
                <Text style={styles.actBtnText}>Deshacer llegada</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[styles.actBtn, styles.actBtnHalf]}
              onPress={() => props.onMove(item)}
              accessibilityRole="button"
              accessibilityLabel="Reagendar (otro horario u otra agenda)"
              testID="appointments.card.move"
            >
              <Text style={styles.actBtnText}>Reagendar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actBtn, styles.actBtnHalf]}
              accessibilityRole="button"
              accessibilityLabel="WhatsApp"
              onPress={() =>
                void openAppointmentWhatsAppReminder({
                  appointment: item,
                  resourceName: props.resourceName,
                  serviceName: props.serviceName,
                  storeName: props.storeName,
                }).then((ok) => {
                  if (ok) {
                    void markAppointmentReminderSent(item.id).then(setReminderSentAt);
                  }
                })
              }
            >
              <Text style={styles.actBtnText}>WhatsApp</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actBtn, styles.actBtnHalf]}
              onPress={() => setMoreOpen((v) => !v)}
              accessibilityRole="button"
              accessibilityLabel="Más acciones"
              accessibilityState={{ expanded: moreOpen }}
              testID="appointments.card.more-actions"
            >
              <Text style={styles.actBtnText}>Más {moreOpen ? '▴' : '▾'}</Text>
            </TouchableOpacity>
          </View>
          {moreOpen ? (
            <View style={styles.morePanel}>
              <TouchableOpacity
                style={styles.moreActionBtn}
                onPress={() => props.onEdit(item)}
                accessibilityRole="button"
                accessibilityLabel="Editar"
              >
                <Text style={styles.btnGhostText}>Editar</Text>
              </TouchableOpacity>
              {props.canWorkOrder ? (
                <TouchableOpacity
                  style={styles.moreActionBtn}
                  disabled={workOrderBusy || linkedWorkOrderId === undefined}
                  onPress={() => {
                    setWorkOrderBusy(true);
                    void Promise.resolve(props.onWorkOrder(item)).finally(() => setWorkOrderBusy(false));
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={linkedWorkOrderId ? 'Abrir OT' : 'Crear OT'}
                  testID="appointments.card.work-order"
                >
                  <Text style={styles.btnGhostText}>
                    {linkedWorkOrderId ? 'Abrir OT' : 'Crear OT'}
                  </Text>
                </TouchableOpacity>
              ) : null}
              {item.status === 'requested' ? (
                <TouchableOpacity style={styles.moreActionBtn} onPress={() => requestStatus('confirmed')}>
                  <Text style={styles.btnGhostText}>Confirmar</Text>
                </TouchableOpacity>
              ) : null}
              <TouchableOpacity style={styles.moreActionBtn} onPress={() => requestStatus('completed')}>
                <Text style={styles.btnGhostText}>Completar sin cobro</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.moreActionBtn}
                onPress={() => requestStatus('no_show')}
                accessibilityRole="button"
                accessibilityLabel="No vino"
                testID="appointments.card.no-show"
              >
                <Text style={styles.btnGhostText}>No vino</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.moreActionDanger}
                onPress={() => requestStatus('cancelled')}
                accessibilityRole="button"
                accessibilityLabel="Cancelar turno"
              >
                <Text style={styles.moreActionDangerText}>Cancelar turno</Text>
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
      ) : (
        <View style={styles.secDivided}>
          <Text style={styles.secTitle}>Acciones</Text>
          <View style={styles.actGrid}>
            {props.canWorkOrder ? (
              <TouchableOpacity
                style={[styles.actBtn, styles.actBtnHalf]}
                disabled={workOrderBusy || linkedWorkOrderId === undefined}
                onPress={() => {
                  setWorkOrderBusy(true);
                  void Promise.resolve(props.onWorkOrder(item)).finally(() => setWorkOrderBusy(false));
                }}
                accessibilityRole="button"
                accessibilityLabel={linkedWorkOrderId ? 'Abrir OT' : 'Crear OT'}
                testID="appointments.card.work-order"
              >
                <Text style={styles.actBtnText}>
                  {linkedWorkOrderId ? 'Abrir OT' : 'Crear OT'}
                </Text>
              </TouchableOpacity>
            ) : null}
            {item.customerId ? (
              <TouchableOpacity
                style={[styles.actBtn, styles.actBtnHalf]}
                onPress={() => props.onOpenCustomer(item.customerId!)}
                accessibilityRole="button"
                accessibilityLabel="Ver ficha del cliente"
              >
                <Text style={styles.actBtnText}>Ver ficha</Text>
              </TouchableOpacity>
            ) : null}
          </View>
          <Text style={styles.hint}>Este turno ya está {STATUS_LABELS[item.status].toLowerCase()}.</Text>
        </View>
      )}
    </ResponsiveFormShell>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: agenda.bg },
  /** Proto .move-bar — contexto del turno en tránsito sobre el board. */
  moveBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: agenda.chooseInk,
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 10,
    marginHorizontal: 14,
    marginTop: 10,
  },
  moveBarText: { flex: 1, minWidth: 0, color: agenda.brandInk, fontSize: 13, fontWeight: '700' },
  moveBarStrong: { fontWeight: '800' },
  moveBarCancel: {
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.4)',
    borderRadius: 8,
    paddingHorizontal: 11,
    paddingVertical: 5,
  },
  moveBarCancelText: { color: agenda.brandInk, fontSize: 12, fontWeight: '700' },
  setupWrap: { flex: 1, paddingHorizontal: 16, paddingTop: 24, justifyContent: 'center' },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: agenda.border,
    backgroundColor: agenda.surface,
    zIndex: 2,
  },
  toolbarMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 10,
  },
  toolbarDateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  dateNav: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: agenda.radius,
    backgroundColor: agenda.surface,
    overflow: 'hidden',
    flexShrink: 1,
  },
  dateNavMobile: { flex: 1, minWidth: 0 },
  dateNavBtn: {
    width: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dateNavBtnText: {
    fontSize: 18,
    lineHeight: 20,
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  dateNavLabel: {
    minWidth: 118,
    paddingHorizontal: 10,
    paddingVertical: 8,
    textAlign: 'center',
    fontSize: 14,
    fontWeight: '700',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
    borderLeftWidth: StyleSheet.hairlineWidth,
    borderRightWidth: StyleSheet.hairlineWidth,
    borderColor: agenda.border,
  },
  dateNavLabelMobile: {
    flex: 1,
    minWidth: 0,
    paddingHorizontal: 8,
    paddingVertical: 8,
    textAlign: 'center',
    fontSize: 14,
    fontWeight: '700',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
    borderLeftWidth: StyleSheet.hairlineWidth,
    borderRightWidth: StyleSheet.hairlineWidth,
    borderColor: agenda.border,
  },
  toolbarActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  toolbarActionsMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap',
  },
  btnSecondary: {
    flexShrink: 0,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: agenda.radius,
    borderWidth: 1,
    borderColor: agenda.border,
    backgroundColor: agenda.surface,
    alignItems: 'center',
  },
  btnSecondaryText: {
    fontSize: 13,
    fontWeight: '600',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  /** Proto .tbtn: "En espera" con badge warn — entry point del panel waitlist. */
  btnWaitlist: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  waitlistBadge: {
    backgroundColor: ticketColors.warn,
    borderRadius: 999,
    paddingHorizontal: 7,
    paddingVertical: 1,
    minWidth: 20,
    alignItems: 'center',
  },
  waitlistBadgeText: {
    fontSize: 10.5,
    fontWeight: '700',
    color: ticketColors.goldInk,
    fontFamily: agendaFonts.sans,
  },
  viewToggle: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: agenda.radius,
    overflow: 'hidden',
    backgroundColor: agenda.surface,
  },
  viewToggleMobile: { flexShrink: 1 },
  viewBtn: {
    paddingHorizontal: 10,
    paddingVertical: 7,
    backgroundColor: 'transparent',
  },
  viewBtnDivider: {
    borderLeftWidth: StyleSheet.hairlineWidth,
    borderLeftColor: agenda.border,
  },
  viewBtnMobile: { paddingHorizontal: 9 },
  viewBtnActive: {
    backgroundColor: agenda.brand,
    borderLeftColor: agenda.brand,
  },
  viewBtnText: { fontSize: 12, fontWeight: '600', color: agenda.ink, fontFamily: agendaFonts.sans },
  viewBtnTextActive: { color: agenda.brandInk },
  callout: {
    marginHorizontal: 14,
    marginTop: 10,
    marginBottom: 0,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: agenda.chooseBg,
    borderWidth: 1,
    borderColor: agenda.chooseBorder,
  },
  calloutText: { fontSize: 12, color: agenda.chooseInk, lineHeight: 17, fontFamily: agendaFonts.sans },
  calloutStrong: { fontWeight: '700', color: agenda.chooseInk },
  /** Hint de discoverability: hueco→nuevo turno, long-press→reagendar. */
  boardHint: {
    fontSize: 11.5,
    color: agenda.ink3,
    paddingHorizontal: 12,
    paddingVertical: 4,
    fontFamily: agendaFonts.sans,
  },
  btn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: agenda.radius,
    borderWidth: 1,
    borderColor: agenda.border,
    backgroundColor: agenda.surface,
  },
  btnMuted: { opacity: 0.45 },
  btnText: { fontSize: 13, fontWeight: '600', color: agenda.ink, fontFamily: agendaFonts.sans },
  btnTextMuted: { color: agenda.ink2 },
  btnPrimary: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: agenda.radius,
    backgroundColor: agenda.brand,
    borderWidth: 1,
    borderColor: agenda.brand,
    minWidth: 96,
    alignItems: 'center',
  },
  btnPrimaryText: { fontSize: 13, fontWeight: '700', color: agenda.brandInk, fontFamily: agendaFonts.sans },
  webReset: Platform.select({
    // Sin outlineStyle:none — los browsers solo muestran el ring en :focus-visible
    // (teclado), y quitándolo rompíamos la navegación por Tab.
    web: { boxShadow: 'none' } as object,
    default: {},
  }),
  boardScroll: { flex: 1, width: '100%', alignSelf: 'stretch', backgroundColor: agenda.surface },
  chipWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 4 },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: agenda.radius,
    backgroundColor: agenda.surface2,
    borderWidth: 1,
    borderColor: agenda.border,
  },
  chipActive: { backgroundColor: agenda.brand, borderColor: agenda.brand },
  chipText: { fontSize: 13, fontWeight: '600', color: agenda.ink, fontFamily: agendaFonts.sans },
  chipMeta: { fontSize: 11, color: agenda.ink2, marginTop: 2 },
  chipTextActive: { color: agenda.brandInk },
  primaryBtn: {
    flex: 1,
    backgroundColor: agenda.brand,
    borderRadius: agenda.radius,
    paddingVertical: 12,
    alignItems: 'center',
  },
  primaryBtnText: { fontWeight: '700', color: agenda.brandInk, fontFamily: agendaFonts.sans },
  secondaryBtn: {
    flex: 1,
    borderRadius: agenda.radius,
    paddingVertical: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: agenda.border,
    backgroundColor: agenda.surface,
  },
  secondaryBtnText: { fontWeight: '600', color: agenda.ink, fontFamily: agendaFonts.sans },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(28,25,23,0.45)',
  },
  modalBackdropWide: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalBackdropMobile: {
    justifyContent: 'flex-end',
  },
  dialogCard: {
    ...agendaPaperSheet,
    borderRadius: 16,
    paddingTop: 16,
    paddingHorizontal: 0,
    paddingBottom: 0,
    zIndex: 1,
    ...Platform.select({
      web: { boxShadow: '0 24px 64px rgba(0,0,0,0.35)' } as object,
      default: {
        shadowColor: '#000',
        shadowOpacity: 0.25,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 8 },
        elevation: 8,
      },
    }),
  },
  sheetCard: {
    backgroundColor: agenda.surface,
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    paddingTop: 12,
    width: '100%',
    overflow: 'hidden',
    zIndex: 1,
  },
  /** Proto .sheet .grabber — handle del bottom sheet mobile. */
  grabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: agenda.borderControl,
    alignSelf: 'center',
    marginBottom: 4,
  },
  formHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: agenda.border,
    flexShrink: 0,
  },
  formTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  formBodyWrap: {
    flexGrow: 0,
    flexShrink: 1,
    minHeight: 0,
  },
  formScroll: {
    flexGrow: 0,
  },
  formScrollContent: {
    paddingBottom: 12,
  },
  formFooter: {
    flexShrink: 0,
    borderTopWidth: 1,
    borderTopColor: agenda.border,
    backgroundColor: agenda.surface,
  },
  closeBox: {
    width: 32,
    height: 32,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: agenda.borderControl,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  closeGlyph: { fontSize: 14, lineHeight: 16, color: agenda.ink2, fontFamily: agendaFonts.sans },
  modalKicker: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.9,
    textTransform: 'uppercase',
    color: agenda.brand,
    marginBottom: 2,
    fontFamily: agendaFonts.sans,
  },
  modalTitle: {
    fontSize: 19,
    fontWeight: '800',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
    lineHeight: 23,
  },
  modalTitleMobile: { fontSize: 17, lineHeight: 21 },
  modalSubtitle: { fontSize: 12.5, color: agenda.ink3, marginTop: 3, lineHeight: 17 },
  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 9,
    paddingVertical: 3,
    borderRadius: 999,
    borderWidth: 1,
  },
  statusPillText: { fontSize: 11, fontWeight: '700', fontFamily: agendaFonts.sans, lineHeight: 14 },
  secTitle: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: agenda.ink3,
    fontFamily: agendaFonts.sans,
    marginBottom: 8,
  },
  detailSec: { marginTop: 2 },
  secDivided: {
    borderTopWidth: 1,
    borderTopColor: agenda.border,
    paddingTop: 16,
    marginTop: 16,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    paddingVertical: 9,
  },
  metaRowBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: agenda.border,
  },
  metaK: {
    fontSize: 13,
    fontWeight: '600',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
    flexShrink: 0,
  },
  metaV: {
    flex: 1,
    textAlign: 'right',
    fontSize: 13,
    fontWeight: '800',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
    fontVariant: ['tabular-nums'],
  },
  metaLink: {
    flexShrink: 1,
    textAlign: 'right',
    fontSize: 12.5,
    fontWeight: '700',
    color: agenda.brand,
    fontFamily: agendaFonts.sans,
  },
  /** Quote origin only — chevron + ≥40px target; phone stays plain tel link. */
  metaQuoteHit: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 2,
    minHeight: 40,
    flexShrink: 1,
    maxWidth: '72%',
  },
  metaQuoteChevron: {
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 18,
    color: agenda.brand,
    fontFamily: agendaFonts.sans,
    marginTop: Platform.OS === 'android' ? -1 : 0,
  },
  statRow: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: 10,
    overflow: 'hidden',
    marginBottom: 16,
  },
  statCell: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 6,
  },
  statCellBorder: {
    borderLeftWidth: StyleSheet.hairlineWidth,
    borderLeftColor: agenda.border,
  },
  statV: {
    fontSize: 15,
    fontWeight: '800',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
    fontVariant: ['tabular-nums'],
  },
  statVOk: { color: agenda.completed.text },
  statL: {
    fontSize: 10.5,
    color: agenda.ink3,
    fontFamily: agendaFonts.sans,
    marginTop: 1,
  },
  noteBox: {
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 11,
    paddingVertical: 9,
    marginBottom: 10,
  },
  noteBoxText: {
    fontSize: 12,
    fontWeight: '600',
    lineHeight: 17,
    fontFamily: agendaFonts.sans,
  },
  /** Proto .info-box — bg --tile (#FAFBFA), no surface2 mint. */
  noteBoxInfo: { backgroundColor: ticketColors.tile, borderColor: agenda.border },
  noteBoxInfoText: { color: agenda.ink2 },
  noteBoxWarn: { backgroundColor: ticketColors.warnBg, borderColor: ticketColors.warnBorder },
  noteBoxWarnText: { color: ticketColors.warn },
  noteBoxOk: { backgroundColor: agenda.completed.bg, borderColor: agenda.completed.border },
  noteBoxOkText: { color: agenda.completed.text },
  noteBoxBad: { backgroundColor: agenda.noShow.bg, borderColor: agenda.noShow.border },
  noteBoxBadText: { color: agenda.noShow.text },
  metaNote: {
    flex: 1,
    textAlign: 'right',
    fontSize: 13,
    fontWeight: '600',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  /** Proto .act-grid — 3 col desktop / 2 col mobile, botones borde ctl. */
  actGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  actBtn: {
    borderWidth: 1,
    borderColor: agenda.borderControl,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: agenda.surface,
    minHeight: 40,
    flexGrow: 1,
    flexShrink: 1,
  },
  actBtnHalf: { flexBasis: '45%' },
  actBtnPri: {
    flexBasis: '100%',
    backgroundColor: agenda.brand,
    borderColor: agenda.brand,
  },
  actBtnText: { fontSize: 12.5, fontWeight: '700', color: agenda.ink, fontFamily: agendaFonts.sans },
  actBtnPriText: {
    fontSize: 13.5,
    fontWeight: '700',
    color: agenda.brandInk,
    fontFamily: agendaFonts.sans,
  },
  btnGhostText: { fontSize: 14, fontWeight: '600', color: agenda.ink, fontFamily: agendaFonts.sans },
  morePanel: {
    marginTop: 10,
    paddingTop: 10,
    gap: 6,
    borderTopWidth: 1,
    borderTopColor: agenda.border,
  },
  moreActionBtn: {
    borderRadius: 10,
    backgroundColor: agenda.surface,
    borderWidth: 1,
    borderColor: agenda.borderControl,
    paddingVertical: 10,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  /** Proto .abtn.danger — borde + texto bad, sin relleno. */
  moreActionDanger: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(220,38,38,0.3)',
    paddingVertical: 10,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  moreActionDangerText: { fontSize: 14, fontWeight: '600', color: ticketColors.bad, fontFamily: agendaFonts.sans },
  hint: { fontSize: 12, color: agenda.ink2, marginTop: 8, marginBottom: 4 },
});
